import {
  useGetUsers,
  getGetUsersQueryOptions,
} from "@/requests/gen/react-query/user";
import { UserRoleEnum } from "@/requests/gen/react-query/fastAPI.schemas";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  createFileRoute,
  Link,
  linkOptions,
  type LinkProps,
} from "@tanstack/react-router";
import { Ban, Plus } from "lucide-react";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ParamDataTableToolbar,
  ParamSearchFilter,
  ParamClearButton,
  ParamDataTable,
  dataTableSearchParsers,
} from "@/components/ui/param-data-table";
import { Badge } from "@/components/ui/badge";
import snakeToCamel from "@/lib/snake-to-camel";
import {
  createLoader,
  parseAsArrayOf,
  parseAsStringEnum,
  useQueryStates,
  type inferParserType,
} from "nuqs";
import { DropdownFilter } from "@/components/ui/dropdown-filter";
import { keepPreviousData } from "@tanstack/react-query";
import { useGetEntitiesInfinite } from "@/requests/gen/react-query/entity";
import { Header } from "@/components/universal/header";
import { useState } from "react";
import { Toggle } from "@/components/ui/toggle";

const userSearchParsers = {
  roles: parseAsArrayOf(parseAsStringEnum(Object.values(UserRoleEnum))),
};

const userSearchAllParsers = {
  ...dataTableSearchParsers,
  ...userSearchParsers,
};

const loadUserSearch = createLoader(userSearchAllParsers);

type UserSearch = inferParserType<typeof userSearchAllParsers>;

function userQueryParams(
  p: UserSearch,
): Parameters<typeof getGetUsersQueryOptions>[0] {
  return {
    page: p.page,
    limit: p.limit,
    search: p.search,
    sort_column: p.sort_column,
    ascending: p.ascending,
    is_deleted: p.is_deleted,
    entity_ids: p.entity_ids,
    roles: p.roles || [],
  };
}

export const Route = createFileRoute("/_private/_admin/user/lookup")({
  loader: ({ context: { queryClient }, location }) =>
    queryClient.prefetchQuery(
      getGetUsersQueryOptions(
        userQueryParams(loadUserSearch(location.searchStr)),
      ),
    ),
  component: UserLookup,
});

// Define the columns for the table
interface UserTableProps {
  link?: LinkProps;
  name: string;
  role: UserRoleEnum;
  email: string;
  entities: string[];
}

// Define the columns headers and accessor keys
const columns: ColumnDef<UserTableProps>[] = [
  { header: "Name", accessorKey: "name" },
  {
    header: "Role",
    accessorKey: "role",

    cell: (value) => {
      return snakeToCamel(value.getValue() as string);
    },
  },
  { header: "Email", accessorKey: "email" },
  {
    header: "Entity",
    accessorKey: "entities",
    cell: ({ row }) => {
      return row.original.entities.map((entity: string) => (
        <Badge key={entity} variant={"outline"} className="rounded-md m-0.5">
          {entity}
        </Badge>
      ));
    },
  },
];

// Main component
function UserLookup() {
  const [queryParams, setQueryParams] = useQueryStates(userSearchAllParsers);

  const users = useGetUsers(userQueryParams(queryParams), {
    query: {
      placeholderData: keepPreviousData,
    },
  });

  const [entitySearch, setEntitySearch] = useState<string | undefined>();
  const entities = useGetEntitiesInfinite(
    {
      search: entitySearch,
      include_ids: queryParams.entity_ids,
    },
    {
      query: {
        placeholderData: keepPreviousData,
        getNextPageParam: (lastPage, allPages) => {
          if (lastPage.items.length === 0) {
            return undefined;
          }
          return allPages.length + 1;
        },
      },
    },
  );

  // Map the data to the table format
  const tableData: UserTableProps[] | undefined = users.data?.items.map(
    (user) => {
      return {
        link: linkOptions({
          to: "/user/$userId",
          params: { userId: String(user.user_id) },
        }),
        name: user.first_name + " " + user.last_name,
        role: user.role,
        email: user.email || "",
        entities: user.user_entities.map(
          (userEntity) => userEntity.entity.name,
        ),
      };
    },
  );

  return (
    <div className="text-left">
      <title>Users - Legrande Health Portal</title>
      <Header crumbs={[{ name: "Users", link: { to: "/user/lookup" } }]} />
      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
          <CardDescription>
            View recent users below. You can search, filter, and click to see
            more details.
          </CardDescription>
          <CardAction>
            <Link to="/user/create">
              <Button variant="default" size="sm">
                <Plus />
                Create User
              </Button>
            </Link>
          </CardAction>
        </CardHeader>
        <CardContent>
          <ParamDataTableToolbar>
            <ParamSearchFilter isFetching={users.isFetching} />
            <DropdownFilter
              title="Role"
              options={[
                {
                  items: Object.values(UserRoleEnum).map((role) => {
                    return { label: snakeToCamel(role), value: role };
                  }),
                },
              ]}
              values={queryParams.roles || []}
              onChange={(values) => setQueryParams({ roles: values })}
            />
            <DropdownFilter
              title="Entity"
              search={entitySearch}
              setSearch={setEntitySearch}
              isFetching={entities.isFetchingNextPage}
              fetchNextPage={entities.fetchNextPage}
              options={[
                {
                  items:
                    entities.data?.pages
                      .flatMap((page) => page.items)
                      .map((entity) => {
                        return {
                          label: entity.name,
                          value: entity.entity_id,
                        };
                      }) ?? [],
                },
              ]}
              values={queryParams.entity_ids}
              onChange={(values) => setQueryParams({ entity_ids: values })}
            />

            <Toggle
              pressed={queryParams.is_deleted || false}
              onPressedChange={(pressed) => {
                setQueryParams({ is_deleted: pressed });
              }}
            >
              <Ban />
              Deactivated
            </Toggle>

            <ParamClearButton />
          </ParamDataTableToolbar>
          <ParamDataTable
            columns={columns}
            data={tableData ?? []}
            isLoading={users.isLoading}
            isError={users.isError}
            count={users.data?.count}
          />
        </CardContent>
      </Card>
    </div>
  );
}
